import React, { useState, useEffect, useRef } from 'react';
import { Reorder, useDragControls, motion } from 'framer-motion';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { MonetaryInput } from './MonetaryInput';
import { TrashIconButton } from './TrashIconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faChevronUp, faChevronDown } from '@/icons';

export interface ExpenseItemData {
    id: string;
    name: string;
    amount: number;
    lineId?: string;
}

interface ExpenseItemProps {
    expense: ExpenseItemData;
    index: number;
    totalExpenses: number;
    onUpdate: (field: keyof ExpenseItemData, value: ExpenseItemData[keyof ExpenseItemData]) => void;
    onRemove: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    autoFocus?: boolean;
    isReadOnly?: boolean;
}

// ReorderItem wrapper for drag and drop
interface ExpenseReorderItemProps extends ExpenseItemProps {
    expenseIndex: number;
    expenses: ExpenseItemData[];
    lastAddedIndex: number | null;
    stableKey: string;
    theme: any;
}

const ExpenseReorderItem: React.FC<ExpenseReorderItemProps> = ({
    expense,
    expenseIndex,
    expenses,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
    lastAddedIndex,
    stableKey,
    theme,
    isReadOnly = false,
}) => {
    const controls = useDragControls();

    return (
        <Reorder.Item
            key={stableKey}
            value={expense}
            dragControls={controls}
            dragListener={false}
            layout
            layoutId={stableKey}
            initial={{ opacity: 0, y: -20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            style={{
                marginBottom: theme.spacing.sm,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.md,
                backgroundColor: theme.colors.surface,
                position: 'relative',
                paddingLeft: isReadOnly ? theme.spacing.md : '40px', // Reserve space for drag handle
                overflow: 'hidden',
            }}
            whileDrag={{
                cursor: 'grabbing',
                zIndex: 500,
            }}
            transition={{
                layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                opacity: { duration: 0.3 },
                y: { duration: 0.3 },
                height: { duration: 0.3 },
            }}
        >
            {/* Drag Handle */}
            {!isReadOnly && (
                <div
                    onPointerDown={(e) => {
                        e.preventDefault();
                        controls.start(e);
                    }}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: theme.spacing.sm,
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'grab',
                        zIndex: 1,
                        color: theme.colors.text.secondary,
                        padding: theme.spacing.xs,
                        pointerEvents: 'auto',
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.style.cursor = 'grabbing';
                    }}
                    onMouseUp={(e) => {
                        e.currentTarget.style.cursor = 'grab';
                    }}
                >
                    <FontAwesomeIcon
                        icon={faGripVertical}
                        style={{
                            fontSize: theme.typography.fontSize.md,
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            )}

            <ExpenseItemForm
                expense={expense}
                index={expenseIndex}
                totalExpenses={expenses.length}
                onUpdate={onUpdate}
                onRemove={onRemove}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                canMoveUp={expenseIndex > 0}
                canMoveDown={expenseIndex < expenses.length - 1}
                autoFocus={lastAddedIndex === expenseIndex}
                isReadOnly={isReadOnly}
            />
        </Reorder.Item>
    );
};

// ExpenseItemForm component - the actual form content
const ExpenseItemForm: React.FC<ExpenseItemProps> = ({
    expense,
    index,
    totalExpenses,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
    canMoveUp = true,
    canMoveDown = true,
    autoFocus = false,
    isReadOnly = false
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);

    // Local state for text input to prevent focus loss
    const [localName, setLocalName] = useState(expense.name || '');
    // Local state for amount to prevent revert on blur
    const [localAmount, setLocalAmount] = useState(expense.amount || 0);
    const nameInputRef = useRef<HTMLInputElement>(null);

    // Sync local state when expense changes from external updates
    useEffect(() => {
        const newNameValue = expense.name || '';
        if (newNameValue !== localName) {
            setLocalName(newNameValue);
        }
    }, [expense.name]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync local amount when expense changes from external updates. BudgetPage's page-level
    // echo suppression + lineId reconciliation (web/src/hooks/useKeyedArrayMirror.ts) already
    // guarantees any prop this component receives is either exactly what it just sent (a
    // same-tick echo with no stale value) or a genuine external change with in-progress edits
    // preserved — so this can trust props unconditionally, same as the `name` field above.
    // (A prior float-epsilon "revert detection" heuristic here suppressed legitimate external
    // resets to a prior value, e.g. applying an expense template — removed.)
    useEffect(() => {
        const newAmountValue = expense.amount || 0;
        if (Math.abs(newAmountValue - localAmount) >= 0.01) {
            setLocalAmount(newAmountValue);
        }
    }, [expense.amount]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-focus name input when requested
    useEffect(() => {
        if (autoFocus && nameInputRef.current) {
            // Small delay to ensure the form is fully rendered
            setTimeout(() => {
                nameInputRef.current?.focus();
            }, 100);
        }
    }, [autoFocus]);

    // Name: update parent immediately; BudgetPage debounces persistence (same pattern as Liability / General).

    // Handler for name input with local state
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalName(value);
        onUpdate('name', value);
    };

    // Handler for amount change - update local state immediately, then propagate to parent
    const handleAmountChange = (value: number) => {
        setLocalAmount(value);
        onUpdate('amount', value);
    };

    return (
        <div style={{ paddingTop: theme.spacing.sm, paddingBottom: theme.spacing.xs, paddingLeft: theme.spacing.md, paddingRight: theme.spacing.md }}>
            {/* Main Row: Name and Amount */}
            <div className="rw-expense-row" style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: theme.spacing.md,
                alignItems: 'center',
                marginBottom: theme.spacing.xs,
            }}>
                {/* Name Input */}
                <input
                    ref={nameInputRef}
                    type="text"
                    value={localName}
                    onChange={handleNameChange}
                    placeholder="Expense name"
                    disabled={isReadOnly}
                    style={{
                        ...themedStyles.input.default,
                        width: '100%',
                    }}
                />

                {/* Monetary Input */}
                <MonetaryInput
                    value={localAmount}
                    onChange={handleAmountChange}
                    unit="month"
                    calculatorType="monthly"
                    disabled={isReadOnly}
                    style={{ textAlign: 'right', width: '180px', maxWidth: '100%' }}
                />

                {/* Buttons Container - Match spacing from asset category components */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: theme.spacing.xs,
                }}>
                    {/* Move Up Button - Always rendered first, hidden when not applicable */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (canMoveUp && onMoveUp) {
                                onMoveUp();
                            }
                        }}
                        disabled={isReadOnly || !canMoveUp || !onMoveUp}
                        style={{
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.inverse,
                            border: 'none',
                            borderRadius: theme.borderRadius.sm,
                            padding: theme.spacing.sm,
                            cursor: (isReadOnly || !canMoveUp || !onMoveUp) ? 'not-allowed' : 'pointer',
                            opacity: (isReadOnly || !canMoveUp || !onMoveUp) ? 0.3 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 'auto',
                            visibility: (canMoveUp && onMoveUp) ? 'visible' : 'hidden',
                            pointerEvents: (canMoveUp && onMoveUp) ? 'auto' : 'none',
                        }}
                        aria-label="Move up"
                    >
                        <FontAwesomeIcon
                            icon={faChevronUp}
                            style={{ fontSize: theme.typography.fontSize.sm }}
                        />
                    </button>

                    {/* Move Down Button - Always rendered, hidden when not applicable */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (canMoveDown && onMoveDown) {
                                onMoveDown();
                            }
                        }}
                        disabled={isReadOnly || !canMoveDown || !onMoveDown}
                        style={{
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.inverse,
                            border: 'none',
                            borderRadius: theme.borderRadius.sm,
                            padding: theme.spacing.sm,
                            cursor: (isReadOnly || !canMoveDown || !onMoveDown) ? 'not-allowed' : 'pointer',
                            opacity: (isReadOnly || !canMoveDown || !onMoveDown) ? 0.3 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 'auto',
                            visibility: (canMoveDown && onMoveDown) ? 'visible' : 'hidden',
                            pointerEvents: (canMoveDown && onMoveDown) ? 'auto' : 'none',
                        }}
                        aria-label="Move down"
                    >
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            style={{ fontSize: theme.typography.fontSize.sm }}
                        />
                    </button>

                    {/* Trash Button */}
                    <TrashIconButton onClick={onRemove} disabled={isReadOnly} />
                </div>
            </div>
        </div>
    );
};

// Export the main component for use with Reorder.Group
export const ExpenseItem: React.FC<ExpenseReorderItemProps> = (props) => {
    return <ExpenseReorderItem {...props} />;
};

